import type { AuthTranslations } from './zh-CN'

export const authEnUS = {
  session: {
    restoring: 'Restoring your session',
    confirming: 'Confirming your sign-in status. Please wait.',
    restoringInline: 'Restoring session…',
    unavailableTitle: 'Unable to confirm your sign-in status',
    unavailableDescription:
      'Try again. If the problem continues, sign out and log in again.',
  },
  fields: {
    account: 'Account',
    accountPlaceholder: 'Username, email, or phone number',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    remember: 'Remember me',
    username: 'Username',
    usernamePlaceholder: 'Enter your username',
    email: 'Email',
    emailPlaceholder: 'Enter your email',
    phone: 'Phone number',
    phonePlaceholder: 'Enter your phone number',
    registeredEmailPlaceholder: 'Enter your registered email',
    confirmPassword: 'Confirm password',
    confirmPasswordPlaceholder: 'Enter your password again',
    newPassword: 'New password',
    newPasswordPlaceholder: 'Enter your new password',
    confirmNewPassword: 'Confirm new password',
    confirmNewPasswordPlaceholder: 'Enter your new password again',
    minimum6: 'At least 6 characters',
    minimum8: 'At least 8 characters',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
  },
  validation: {
    invalidEmail: 'Enter a valid email address',
    invalidPhone: 'Enter a valid mainland China mobile number',
    passwordMismatch: 'The passwords do not match',
    minimum8: '{{field}} must be at least 8 characters',
    required: 'Enter {{field}}',
  },
  login: {
    title: 'Welcome back',
    description: 'Sign in to the UFP digital learning platform',
    noAccount: 'New to Perceptio?',
    register: 'Create an account',
    forgotPassword: 'Forgot password?',
    submit: 'Log in',
    registered: 'Registration complete. Please log in.',
    verified: 'Email verified. Please log in.',
    reset: 'Password updated. Log in with your new password.',
    failed: 'Login failed. Check your account and password.',
  },
  phoneVerification: {
    title: 'Phone verification',
    description:
      'Verify your phone number to sign in with your phone and password.',
    phone: 'Phone number',
    phonePlaceholder: 'Enter your phone number',
    code: 'Verification code',
    codePlaceholder: 'Enter the 6-digit code',
    requestCode: 'Get code',
    resendCode: 'Get code again',
    requesting: 'Sending…',
    confirming: 'Verifying…',
    countdown: 'Try again in {{seconds}}s',
    confirm: 'Verify phone number',
    verified: 'Phone number verified',
    unverified: 'Phone number not verified',
    requestSuccess: 'Enter the SMS verification code.',
    confirmSuccess: 'Phone number verified.',
    requestFailed: 'Unable to get a verification code. Try again.',
    confirmFailed: 'Phone verification failed. Check the code and try again.',
    invalidPhone: 'Enter a valid phone number.',
    invalidCode: 'Enter the 6-digit verification code.',
  },
  register: {
    title: 'Create an account',
    emailDescription: 'Verify your email before logging in.',
    localDescription: 'Log in immediately after registration.',
    hasAccount: 'Already have an account?',
    login: 'Log in',
    emailMode: 'Email verification',
    localMode: 'Quick registration',
    localHint:
      'Quick registration creates an account without email verification. Use email verification in production.',
    submit: 'Create account',
    failed: 'Registration failed. Try again.',
    invalidDetails: 'Check the information you entered.',
    conflict: 'That username, email, or phone number is already registered.',
    checkEmail: 'Check your email',
    verificationSent:
      'We sent a verification link to {{email}}. Verify it to log in.',
    created: 'Account created. Confirm your identity by email.',
    back: 'Back to registration',
    sending: 'Sending...',
    resend: 'Resend verification email',
    backLogin: 'Back to login',
  },
  forgot: {
    title: 'Forgot password',
    description: 'Enter your registered email and we will send a reset link.',
    submit: 'Send reset link',
    failed: 'Unable to send the email. Try again.',
    backLogin: 'Back to login',
    sentTitle: 'Reset email sent',
    sentDescription:
      'If the account exists, you will receive a password reset email.',
    sentAlert: 'Check your inbox and open the reset link.',
  },
  reset: {
    invalidTitle: 'Invalid reset link',
    invalidDescription: 'The link has no access token or has expired.',
    requestAgain: 'Request another link',
    title: 'Set a new password',
    description: 'Create a new password with at least 8 characters.',
    submit: 'Update password',
    failed: 'Unable to update the password. Try again.',
    expired: 'The reset link expired. Request another one.',
  },
  verify: {
    failedTitle: 'Email verification failed',
    invalidDescription: 'The verification link is invalid or expired.',
    invalidTitle: 'Invalid link',
    missingToken:
      'The verification link has no access token. Request another one.',
    backRegister: 'Back to registration',
    registerAgain: 'Register again',
    backLogin: 'Back to login',
    failed: 'Unable to verify the email. Try again.',
    expired: 'The verification link expired. Request another one.',
    incomplete:
      'Email verification is incomplete. Check the link in your email.',
    confirmingTitle: 'Verifying your email…',
    confirmingDescription: 'Please wait while we finish verification.',
  },
  brand: {
    platformLabel: 'Education SaaS platform',
    defaultHeadline: 'Smarter teaching.\nSmarter learning.',
    defaultDescription:
      'Manage courses, questions, assignments, and AI-assisted grading in one workspace.',
    featureChat: 'AI chat',
    featureChatDescription: 'A learning assistant grounded in course context',
    featureCourses: 'Courses and questions',
    featureCoursesDescription:
      'Structured course, program, and question management',
    featureGrading: 'Intelligent grading',
    featureGradingDescription:
      'AI-assisted scoring and one-click grade publishing',
    headline: 'Simpler teaching management. Clearer learning progress.',
    featureTeaching: 'Unified teaching workspace',
    featureTeachingDescription:
      'Manage courses, questions, assignments, and grading',
    featureAi: 'AI-assisted teaching',
    featureAiDescription:
      'Intelligent Q&A, content processing, and learning support',
    featureOrganization: 'Organization and access control',
    featureOrganizationDescription:
      'Connect schools, teachers, students, and families',
    copyright: '© 2026 Perceptio · Built for education',
    studentsAlt: 'University students celebrating graduation',
    programmeHeadline: 'Your bridge to leading\nuniversities worldwide',
    programmeTagline: 'Global talent, limitless possibilities',
    programmeDescription:
      'Empowering learners through innovative, rigorous, and fair qualifications that open doors to higher education, careers, and personal growth.',
  },
} as const satisfies AuthTranslations
